import {
  ThinkingDots,
  ThinkingLabel,
  ThinkingLine,
  ThinkingTone,
} from './ThinkingIndicator.styled';

type Props = {
  /**
   * What the assistant is doing right now, already in words. `null` leaves the
   * dots alone - which only happens where the dots can stand by themselves
   * (see `alwaysShow`).
   */
  label?: string | null;
  /** Which bubble it is drawn in. */
  tone: ThinkingTone;
  /** Draw the dots even with nothing to say. */
  alwaysShow?: boolean;
  /** Set when the line follows text rather than standing alone, for the gap. */
  inline?: boolean;
};

/**
 * "Checking your menu..." and the three dots, one component for both chats.
 *
 * The words differ between them - Viktor checks OUR data, the support bot
 * checks YOUR menu - but the phrasing, the metrics and the animation are the
 * same object, which is the point of it living here rather than being written
 * twice. Both are drawn inside a bubble in the thread, where the answer will
 * land.
 *
 * Silence is allowed: Viktor says nothing while he WRITES, because the answer
 * growing above says it better. There the dots keep the empty bubble alive on
 * their own (`alwaysShow`); everywhere else a line with nothing to say is not
 * drawn at all.
 */
export const ThinkingIndicator = ({
  label,
  tone,
  alwaysShow,
  inline,
}: Props) => {
  if (!label && !alwaysShow) return null;

  return (
    <ThinkingLine
      $tone={tone}
      $inline={inline}
      /* One live region for the whole run: the phase replaces itself in
         place, so a screen reader hears each new sentence once. */
      role="status"
      aria-label={label ? undefined : 'Thinking'}
    >
      {/* Keyed on the words so React swaps the element and the fade runs. */}
      {label && <ThinkingLabel key={label}>{label}</ThinkingLabel>}
      <ThinkingDots aria-hidden>
        <span />
        <span />
        <span />
      </ThinkingDots>
    </ThinkingLine>
  );
};
